import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import api from '../../services/api';

const MINUTE_FIELDS = new Set([
  'thresholdMinutes',
  'afterMinutes',
  'cooldownMinutes',
  'delayMinutes',
  'waitAnalyzeEveryMinutes',
  'waitLookbackMinutes',
  'waitCompareWindowMinutes',
]);

/** Human-readable duration under minute inputs when value > 60. */
function formatMinutesHint(minutes) {
  const n = Math.round(Number(minutes));
  if (!Number.isFinite(n) || n <= 60) return null;
  const hours = Math.floor(n / 60);
  const mins = n % 60;
  if (mins === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
  if (hours === 0) return `${mins} min`;
  const hourLabel = hours === 1 ? '1 hora' : `${hours} horas`;
  return `${hourLabel} ${mins} min`;
}

function MinutesField({
  inputId,
  label,
  value,
  disabled,
  min = 0,
  onChange,
}) {
  const hint = formatMinutesHint(value);
  return (
    <div className="flex flex-column gap-2">
      <label className="font-semibold" htmlFor={inputId}>{label}</label>
      <InputNumber
        inputId={inputId}
        className="w-full"
        inputClassName="w-full"
        value={value}
        disabled={disabled}
        min={min}
        onValueChange={(e) => onChange(e.value ?? 0)}
      />
      {hint ? (
        <small className="admin-page-hint" style={{ marginTop: -4 }}>{hint}</small>
      ) : null}
    </div>
  );
}

const EVENTS = [
  {
    id: 'waitDown',
    title: 'Bajó la espera',
    hint: 'Standard de pasajeros. Compara el promedio de la ventana actual contra el de hace “mirar atrás”. Avisa si bajó al menos estos minutos.',
    fields: ['thresholdMinutes', 'cooldownMinutes', 'delayMinutes'],
  },
  {
    id: 'waitUp',
    title: 'Subió la espera',
    hint: 'Standard de pasajeros. Compara el promedio de la ventana actual contra el de hace “mirar atrás”. Avisa si subió al menos estos minutos.',
    fields: ['thresholdMinutes', 'cooldownMinutes', 'delayMinutes'],
  },
  {
    id: 'liveSharing',
    title: 'Hay gente compartiendo',
    hint: 'Cuando hay al menos estas personas en vivo en un cruce que el usuario sigue. No se avisa a quien está compartiendo.',
    fields: ['minPeople', 'cooldownMinutes', 'delayMinutes'],
    fieldLabels: { minPeople: 'Personas en vivo' },
  },
  {
    id: 'timedCrossings',
    title: 'Ya hay tiempos medidos',
    hint: 'Avisa cuando al menos estas personas distintas ya terminaron de cruzar y reportaron cuánto tardaron (live track completado) en la ventana reciente, en un cruce que el usuario sigue. No se avisa a quien midió. Es distinto de “Hay gente compartiendo”, que es gente todavía en vivo.',
    fields: ['minPeople', 'afterMinutes', 'cooldownMinutes', 'delayMinutes'],
    fieldLabels: {
      minPeople: 'Personas que midieron',
      afterMinutes: 'Ventana reciente (min)',
    },
  },
  {
    id: 'nearLine',
    title: 'Estás cerca de la fila',
    fromPhone: true,
    hint: 'Usa la posición de quien ya está compartiendo, si todavía no entra a la zona de cruce y está dentro de este radio del pin.',
    fields: ['radiusMeters', 'cooldownMinutes', 'delayMinutes'],
  },
  {
    id: 'forgotLive',
    title: 'Se te olvidó cerrar el cruce',
    fromPhone: true,
    hint: 'Un recordatorio a quien sigue en vivo después de estos minutos. Una vez por sesión.',
    fields: ['afterMinutes', 'cooldownMinutes', 'delayMinutes'],
    fieldLabels: { afterMinutes: 'Minutos en vivo' },
  },
  {
    id: 'dailySummary',
    title: 'Resumen del día',
    hint: 'Una vez al día, a esta hora en la zona configurada, para quien tiene cruce favorito o extra.',
    fields: ['hour', 'minute', 'cooldownMinutes', 'delayMinutes'],
  },
];

const FIELD_LABELS = {
  thresholdMinutes: 'Cambio mínimo (min)',
  minPeople: 'Personas',
  radiusMeters: 'Radio (m)',
  afterMinutes: 'Minutos',
  hour: 'Hora',
  minute: 'Minuto',
  cooldownMinutes: 'No repetir antes de (min)',
  delayMinutes: 'Esperar antes de enviar (min)',
};

const STATUS_OPTIONS = [
  { label: 'Todos los estados', value: 'all' },
  { label: 'Preparadas', value: 'scheduled' },
  { label: 'Enviadas', value: 'sent' },
  { label: 'Descartadas', value: 'skipped' },
  { label: 'Fallidas', value: 'failed' },
];

const EVENT_OPTIONS = [
  { label: 'Todos los avisos', value: 'all' },
  ...EVENTS.map((event) => ({ label: event.title, value: event.id })),
  { label: 'Aviso oficial', value: 'official' },
  { label: 'Te respondieron', value: 'reply' },
];

const EVENT_TITLES = Object.fromEntries(EVENT_OPTIONS.map((item) => [item.value, item.label]));

const STATUS_META = {
  scheduled: { label: 'Preparada', severity: 'info' },
  sent: { label: 'Enviada', severity: 'success' },
  skipped: { label: 'Descartada', severity: 'warning' },
  failed: { label: 'Falló', severity: 'danger' },
};

function emptyRules() {
  const events = {};
  for (const event of EVENTS) {
    events[event.id] = {
      enabled: true,
      thresholdMinutes: 15,
      minPeople: event.id === 'timedCrossings' ? 3 : 1,
      radiusMeters: 800,
      afterMinutes: event.id === 'timedCrossings' ? 60 : 45,
      hour: 19,
      minute: 0,
      cooldownMinutes: 120,
      delayMinutes: 5,
    };
  }
  return {
    dispatchEnabled: true,
    timezone: 'America/Tijuana',
    waitAnalyzeEveryMinutes: 30,
    waitLookbackMinutes: 60,
    waitCompareWindowMinutes: 15,
    events,
  };
}

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
}

export function PushRulesPanel({ toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState(emptyRules);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/push_notices/rules');
        setRules({ ...emptyRules(), ...data, events: { ...emptyRules().events, ...(data?.events || {}) } });
      } catch {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las reglas de avisos' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const setEvent = (id, patch) => {
    setRules((prev) => ({
      ...prev,
      events: { ...prev.events, [id]: { ...prev.events[id], ...patch } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/push_notices/rules', rules);
      setRules({ ...emptyRules(), ...data, events: { ...emptyRules().events, ...(data?.events || {}) } });
      toast.current?.show({ severity: 'success', summary: 'Listo', detail: 'Reglas de avisos guardadas' });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron guardar las reglas' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="admin-page-hint mb-4">
        Cada aviso se prepara primero y se envía después de los minutos de espera. El cruce tiene que estar en el favorito o en los extras, y el usuario tiene que tener ese aviso encendido.
      </p>
      <div className="flex align-items-center gap-3 mb-4">
        <InputSwitch
          checked={rules.dispatchEnabled}
          disabled={loading || saving}
          onChange={(e) => setRules((prev) => ({ ...prev, dispatchEnabled: Boolean(e.value) }))}
        />
        <span>{rules.dispatchEnabled ? 'Envío automático prendido' : 'Envío en pausa: la cola se llena, pero no sale nada'}</span>
      </div>
      <div className="flex flex-column gap-2 mb-4" style={{ maxWidth: 360 }}>
        <label className="font-semibold" htmlFor="push-tz">Zona del resumen del día</label>
        <InputText
          id="push-tz"
          value={rules.timezone}
          disabled={loading || saving}
          onChange={(e) => setRules((prev) => ({ ...prev, timezone: e.target.value }))}
        />
      </div>
      <div className="mb-4 pb-3" style={{ borderBottom: '1px solid var(--surface-border)' }}>
        <strong>Análisis de espera (Bajó / Subió)</strong>
        <p className="admin-page-hint mt-2 mb-3">
          Cada X minutos se promedia el standard de pasajeros en la ventana actual y se compara
          con el promedio de la misma ventana hace “mirar atrás”. El umbral de cada aviso sigue
          decidiendo si el cambio vale la pena avisar.
        </p>
        <div className="grid">
          <div className="col-12 md:col-4">
            <MinutesField
              inputId="wait-analyze-every"
              label="Analizar cada (min)"
              value={rules.waitAnalyzeEveryMinutes}
              disabled={loading || saving}
              min={1}
              onChange={(v) => setRules((prev) => ({ ...prev, waitAnalyzeEveryMinutes: v }))}
            />
          </div>
          <div className="col-12 md:col-4">
            <MinutesField
              inputId="wait-lookback"
              label="Mirar atrás (min)"
              value={rules.waitLookbackMinutes}
              disabled={loading || saving}
              min={5}
              onChange={(v) => setRules((prev) => ({ ...prev, waitLookbackMinutes: v }))}
            />
          </div>
          <div className="col-12 md:col-4">
            <MinutesField
              inputId="wait-compare-window"
              label="Ventana de promedio (min)"
              value={rules.waitCompareWindowMinutes}
              disabled={loading || saving}
              min={1}
              onChange={(v) => setRules((prev) => ({ ...prev, waitCompareWindowMinutes: v }))}
            />
          </div>
        </div>
      </div>
      {EVENTS.map((event) => {
        const value = rules.events[event.id];
        return (
          <div key={event.id} className="mb-4 pb-3" style={{ borderBottom: '1px solid var(--surface-border)' }}>
            <div className="flex align-items-center gap-3 mb-2">
              <InputSwitch
                checked={value.enabled}
                disabled={loading || saving}
                onChange={(e) => setEvent(event.id, { enabled: Boolean(e.value) })}
              />
              <strong>{event.title}</strong>
              {event.fromPhone ? (
                <Tag value="Desde el teléfono" severity="secondary" />
              ) : null}
            </div>
            <p className="admin-page-hint mt-0 mb-3">{event.hint}</p>
            <div className="grid">
              {event.fields.map((field) => {
                const label = event.fieldLabels?.[field] || FIELD_LABELS[field];
                return (
                <div key={field} className="col-12 md:col-4">
                  {MINUTE_FIELDS.has(field) ? (
                    <MinutesField
                      inputId={`${event.id}-${field}`}
                      label={label}
                      value={value[field]}
                      disabled={loading || saving || !value.enabled}
                      min={0}
                      onChange={(v) => setEvent(event.id, { [field]: v })}
                    />
                  ) : (
                    <div className="flex flex-column gap-2">
                      <label className="font-semibold" htmlFor={`${event.id}-${field}`}>{label}</label>
                      <InputNumber
                        inputId={`${event.id}-${field}`}
                        className="w-full"
                        inputClassName="w-full"
                        value={value[field]}
                        disabled={loading || saving || !value.enabled}
                        min={0}
                        onValueChange={(e) => setEvent(event.id, { [field]: e.value ?? 0 })}
                      />
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          </div>
        );
      })}
      <Button label="Guardar avisos" disabled={loading || saving} loading={saving} onClick={save} />
    </div>
  );
}

export function PushQueuePanel({ toast }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ scheduled: 0, sent: 0, skipped: 0, failed: 0 });
  const [status, setStatus] = useState('all');
  const [event, setEvent] = useState('all');
  const [q, setQ] = useState('');
  const [day, setDay] = useState('today');

  const load = async (next = {}) => {
    const params = {
      status,
      event,
      q,
      day,
      ...next,
    };
    try {
      const { data } = await api.get('/push_notices/outbox', { params });
      setRows(data.items || []);
      setSummary(data.summary || { scheduled: 0, sent: 0, skipped: 0, failed: 0 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la cola de avisos' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, event, day]);

  const discard = async (row) => {
    try {
      await api.post(`/push_notices/outbox/${row.id}/skip`);
      await load();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo descartar' });
    }
  };

  return (
    <div>
      <p className="admin-page-hint mb-3">
        Hoy: {summary.scheduled} preparadas, {summary.sent} enviadas, {summary.skipped} descartadas, {summary.failed} fallidas.
        Una preparada sale cuando llega su hora, si el motivo sigue siendo cierto.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Dropdown value={day} options={[{ label: 'Hoy', value: 'today' }, { label: 'Todo', value: 'all' }]} onChange={(e) => setDay(e.value)} />
        <Dropdown value={status} options={STATUS_OPTIONS} onChange={(e) => setStatus(e.value)} />
        <Dropdown value={event} options={EVENT_OPTIONS} onChange={(e) => setEvent(e.value)} />
        <InputText value={q} placeholder="Usuario, cruce o ciudad" onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
        <Button type="button" label="Buscar" outlined onClick={() => load()} />
      </div>
      <DataTable value={rows} loading={loading} emptyMessage="Todavía no hay avisos en este filtro" size="small" stripedRows>
        <Column header="Cuándo" body={(row) => when(row.preparedAt)} style={{ width: '8rem' }} />
        <Column header="Envío" body={(row) => when(row.sendAfter)} style={{ width: '8rem' }} />
        <Column
          header="Aviso"
          body={(row) => (
            <span className="flex align-items-center gap-2">
              <span>{EVENT_TITLES[row.event] || row.event}</span>
              {EVENTS.find((event) => event.id === row.event)?.fromPhone ? (
                <Tag value="Desde el teléfono" severity="secondary" />
              ) : null}
            </span>
          )}
        />
        <Column field="userLabel" header="Usuario" />
        <Column header="Lugar" body={(row) => [row.crossingName, row.cityName].filter(Boolean).join(' · ') || '—'} />
        <Column field="trigger" header="Por qué" />
        <Column
          header="Filtros"
          body={(row) => (
            <span className="admin-page-hint">{(row.filters || []).join(' · ') || '—'}</span>
          )}
        />
        <Column
          header="Estado"
          body={(row) => {
            const meta = STATUS_META[row.status] || { label: row.status, severity: 'secondary' };
            return (
              <div className="flex flex-column gap-1">
                <Tag value={meta.label} severity={meta.severity} />
                {row.skipReason ? <small>{row.skipReason}</small> : null}
              </div>
            );
          }}
        />
        <Column
          header=""
          body={(row) => row.status === 'scheduled' ? (
            <Button type="button" label="Descartar" text onClick={() => discard(row)} />
          ) : null}
        />
      </DataTable>
    </div>
  );
}
